import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, useEffect, useState } from 'react';
import { cn } from '@/utils/classnames';
import { Sidebar, SIDEBAR_ITEMS } from './Sidebar';
import { TopBar } from './TopBar';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Drawer } from '@/components/Drawer';
import { SkeletonRow } from '@/components/SkeletonRow';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useFailedFeishuEventAlerts } from '@/hooks/useFailedFeishuEventAlerts';

/**
 * Three-region shell:
 *  - Sidebar (256px desktop, 64px tablet, hidden on mobile → drawer)
 *  - TopBar (sticky, h-12 — matches docs/frontend overview design)
 *  - <main> wrapping <AnimatePresence mode="wait"> + <Outlet />
 *
 * Mobile (<640px) replaces the sidebar with a drawer-style flyout (TopBar
 * hamburger) and a 5-tab bottom nav.
 */
export function AppShell() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  useFailedFeishuEventAlerts();

  // Close the mobile drawer whenever the user navigates away.
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F4F5F7] text-on-surface">
      {/* Desktop sidebar (≥1024px) */}
      <div className="hidden lg:block">
        <Sidebar variant="expanded" />
      </div>
      {/* Tablet sidebar (640-1023px) */}
      <div className="hidden md:block lg:hidden">
        <Sidebar variant="collapsed" />
      </div>

      {/* Mobile drawer-style sidebar (<640px) */}
      {isMobile ? (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          side="left"
          width={280}
          hideHeader
          noPadding
        >
          <div className="relative h-full">
            <Sidebar variant="expanded" />
          </div>
        </Drawer>
      ) : null}

      <div className={cn('flex min-h-screen flex-col', 'md:pl-16 lg:pl-64')}>
        <TopBar onToggleSidebar={() => setMobileOpen((v) => !v)} />
        <main className="flex-1 px-container-padding py-container-padding pb-24 md:pb-container-padding">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <Suspense
                fallback={
                  <div className="rounded border border-outline-variant bg-surface p-container-padding">
                    <SkeletonRow count={6} cells={4} />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Mobile bottom tab bar (<640px) */}
        <nav
          aria-label="移动端底部导航"
          className={cn(
            'fixed bottom-0 left-0 right-0 z-30 grid grid-cols-5 border-t border-outline-variant bg-surface-container-lowest md:hidden',
          )}
        >
          {SIDEBAR_ITEMS.filter(
            (item) => item.exact || item.to === '/manifest' || item.to === '/ingress' || item.to === '/ops/feishu-events' || item.to === '/ops/metrics',
          ).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-unit py-stack-sm font-label-caps text-label-caps uppercase',
                  isActive ? 'text-primary' : 'text-on-surface-variant',
                )
              }
            >
              <MaterialIcon name={item.icon} size={18} />
              <span className="truncate text-[10px]">{item.label.split(' ')[0]}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
